import type { AnimatableValue, Keyframe } from './schema';

function cubicBezierY(t: number, y1: number, y2: number): number {
  const inv = 1 - t;
  return 3 * inv * inv * t * y1 + 3 * inv * t * t * y2 + t * t * t;
}

export function interpolationProgress(t: number, keyframe: Keyframe): number {
  const clamped = Math.max(0, Math.min(1, t));
  switch (keyframe.interpolation) {
    case 'hold': return 0;
    case 'ease-in': return clamped * clamped;
    case 'ease-out': return 1 - (1 - clamped) ** 2;
    case 'ease-in-out': return clamped < 0.5 ? 2 * clamped * clamped : 1 - ((-2 * clamped + 2) ** 2) / 2;
    case 'overshoot': {
      const c = 1.70158;
      return 1 + (c + 1) * (clamped - 1) ** 3 + c * (clamped - 1) ** 2;
    }
    case 'bounce': {
      const n1 = 7.5625;
      const d1 = 2.75;
      let x = clamped;
      if (x < 1 / d1) return n1 * x * x;
      if (x < 2 / d1) { x -= 1.5 / d1; return n1 * x * x + 0.75; }
      if (x < 2.5 / d1) { x -= 2.25 / d1; return n1 * x * x + 0.9375; }
      x -= 2.625 / d1;
      return n1 * x * x + 0.984375;
    }
    case 'bezier': return cubicBezierY(clamped, keyframe.bezier?.[1] ?? 0.25, keyframe.bezier?.[3] ?? 0.75);
    default: return clamped;
  }
}

function mix(a: AnimatableValue, b: AnimatableValue, t: number): AnimatableValue {
  if (typeof a === 'number' && typeof b === 'number') return a + (b - a) * t;
  if (Array.isArray(a) && Array.isArray(b) && a.length === b.length) {
    return a.map((value, index) => value + ((b[index] as number) - value) * t) as [number, number] | [number, number, number];
  }
  return t < 1 ? a : b;
}

export function evaluateKeyframes(keyframes: Keyframe[] | undefined, time: number, fallback: AnimatableValue): AnimatableValue {
  if (!keyframes?.length) return fallback;
  const frames = [...keyframes].sort((a, b) => a.time - b.time);
  if (time <= frames[0].time) return frames[0].value;
  const last = frames.at(-1)!;
  if (time >= last.time) return last.value;
  const nextIndex = frames.findIndex((frame) => frame.time >= time);
  const previous = frames[nextIndex - 1];
  const next = frames[nextIndex];
  const progress = interpolationProgress((time - previous.time) / (next.time - previous.time), previous);
  return mix(previous.value, next.value, progress);
}

export function upsertKeyframe(keyframes: Keyframe[], keyframe: Keyframe, epsilon = 0.0001): Keyframe[] {
  const next = keyframes.filter((item) => item.id !== keyframe.id && Math.abs(item.time - keyframe.time) > epsilon);
  next.push(keyframe);
  return next.sort((a, b) => a.time - b.time);
}
